import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, CreditCard, Smartphone, Check, Bitcoin } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/formatters';
import { convertFCFAToEUR } from '../../utils/currencyConverter';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { checkOrderPaymentStatus } from '../../services/shop/orderPaymentService';
import { initiateOrderPayment } from '../../services/shop/orderPaymentService';
// import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { useOrderStatus } from '../../hooks/useOrderStatus';
import { supabase } from '../../lib/supabase';
import { sendOrderConfirmationEmail } from '../../services/shopEmailService';

// Montant minimum pour les paiements internationaux
const MIN_INTERNATIONAL_PAYMENT_AMOUNT = 1500;

// Méthodes de paiement disponibles
const PAYMENT_METHODS = [
  { 
    id: 'mtn', 
    name: 'MTN & Orange Money', 
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/avatars//om-momo.jpg',
    icon: <Smartphone className="h-5 w-5" />,
    description: 'Paiement mobile (Cameroun)'
  },
  {
    id: 'paypal', 
    name: 'PayPal',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logos/images.png',
    icon: <CreditCard className="h-5 w-5" />,
    description: 'Paiement international sécurisé',
    minAmount: MIN_INTERNATIONAL_PAYMENT_AMOUNT
  },
  {
    id: 'stripe', 
    name: 'Cartes Bancaires',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logos/card.jpg',
    icon: <CreditCard className="h-5 w-5" />,
    description: 'Visa, Mastercard, etc.',
    minAmount: MIN_INTERNATIONAL_PAYMENT_AMOUNT
  },
  {
    id: 'plisio', 
    name: 'Cryptomonnaies',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logos/crypto.png',
    icon: <Bitcoin className="h-5 w-5" />,
    description: 'Bitcoin, Ethereum, etc.',
    minAmount: MIN_INTERNATIONAL_PAYMENT_AMOUNT
  },
];

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: {
    id: string;
    order_number: string;
    total_amount: number;
    customer_name: string;
    customer_email: string;
  };
  onPaymentSuccess: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  order,
  onPaymentSuccess
}) => {
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>();
  const { toast } = useToast();
  
  // Suivre le statut de la commande et fermer le modal dès que le statut passe à "paid"
  useOrderStatus({
    orderId: order.id,
    onSuccess: () => {
      console.log('[PaymentModal] onSuccess déclenché, préparation de l\'envoi d\'email et de la redirection...');
      toast({ title: "Paiement réussi !", description: "Votre commande a été confirmée." });
      setProcessing(false); // Arrêter l'indicateur de traitement
      
      // Envoyer l'email de confirmation de commande AVANT la redirection
      console.log('[PaymentModal] Envoi de l\'email de confirmation pour la commande:', order.id);
      
      // Préparer la redirection immédiate sans attendre l'envoi d'email
      const orderId = order.id;
      const redirectUrl = `/boutique/confirmation/${orderId}`;
      console.log('[PaymentModal] Redirection immédiate vers:', redirectUrl);
      
      // Stocker l'ID de commande dans localStorage pour la page de confirmation
      localStorage.setItem('last_order_code', order.id.toString());
      
      // Redirection immédiate sans attendre l'envoi d'email
      toast({ title: "Paiement confirmé", description: "Redirection vers la page de confirmation..." });
      redirectToConfirmationPage();
      
      // Fonction de redirection vers la page de confirmation
      function redirectToConfirmationPage() {
        try {
          // Utiliser directement l'ID numérique pour la redirection
          const orderId = order.id;
          console.log('[PaymentModal] Détails de la commande pour redirection:', { 
            id: order.id, 
            order_number: order.order_number,
            status: 'paid'
          });
          
          // Forcer la redirection avec l'ID numérique uniquement
          const redirectUrl = `/boutique/confirmation/${orderId}`;
          console.log('[PaymentModal] Redirection vers:', redirectUrl);
          
          // Utiliser replace pour éviter les problèmes d'historique
          window.location.replace(redirectUrl);
        } catch (err) {
          console.error('[PaymentModal] Erreur lors de la redirection:', err);
          // Fallback en cas d'erreur - rediriger vers la boutique
          window.location.href = '/boutique';
        }
      }
    },

    onError: (error) => {
      console.error('[PaymentModal] Erreur de suivi de commande:', error);
      toast({ title: "Erreur de suivi", description: error.message, variant: "destructive" });
      setProcessing(false);
    },
  });

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setPhoneError(null);
  };

  const handlePaymentMethodSelect = (method: string) => {
    setSelectedPayment(method);
    
    // Réinitialiser les erreurs de téléphone
    if (method !== 'mtn') {
      setPhoneError(null);
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPayment) {
      toast({ title: "Méthode de paiement requise", description: "Veuillez sélectionner une méthode de paiement", variant: "destructive" });
      return;
    }
    
    // Vérifier le numéro de téléphone pour le paiement mobile
    if (selectedPayment === 'mtn' && !phoneNumber) {
      setPhoneError('Numéro de téléphone requis');
      return;
    }
    
    // Vérifier le format du numéro pour MTN/Orange (format camerounais)
    if (selectedPayment === 'mtn' && !/^(237)?[6-9][0-9]{8}$/.test(phoneNumber)) {
      setPhoneError('Format invalide. Exemple: 237612345678');
      return;
    }
    
    setProcessing(true);
    
    try {
      // Utiliser le service de paiement spécifique à la boutique
      const response = await initiateOrderPayment(order, {
        paymentMethod: selectedPayment,
        phoneNumber: phoneNumber
      });
      
      // Stocker la référence de paiement pour le suivi
      setPaymentReference(response.payment_reference);
      
      // Si c'est un paiement qui nécessite une redirection (PayPal, Stripe, etc.)
      if (response.payment_url && ['paypal', 'stripe', 'plisio'].includes(selectedPayment)) {
        window.open(response.payment_url, '_blank');
      }
      
      toast({ 
        title: "Paiement initié", 
        description: "Veuillez suivre les instructions pour finaliser votre paiement", 
        variant: "default" 
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du paiement:', error);
      toast({ 
        title: "Erreur", 
        description: error instanceof Error ? error.message : "Une erreur est survenue lors de l'initialisation du paiement", 
        variant: "destructive" 
      });
      setProcessing(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] rounded-xl bg-black/80 border border-white/10 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-xl font-bold text-white">Paiement de votre commande</Dialog.Title>
            <Dialog.Close asChild>
              <button className="rounded-full p-1 hover:bg-white/10">
                <X className="h-5 w-5 text-white" />
              </button>
            </Dialog.Close>
          </div>
          
          <div className="mb-6">
            <div className="bg-white/5 p-4 rounded-lg mb-4">
              <p className="text-sm text-white/70">Montant à payer</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(order.total_amount)} FCFA</p>
              <p className="text-xs text-white/50">Commande #{order.order_number}</p>
            </div>
          </div>
          
          <form onSubmit={handlePaymentSubmit}>
            <p className="text-lg font-medium text-white/90 mb-4">Choisissez votre méthode de paiement</p>
            <div className="space-y-3">
              {PAYMENT_METHODS.map(method => (
                <div key={method.id}>
                  <button
                    type="button"
                    onClick={() => handlePaymentMethodSelect(method.id)}
                    disabled={method.minAmount && order.total_amount < method.minAmount}
                    className={`relative w-full flex items-center p-3 rounded-lg border-2 transition-all overflow-hidden
                      ${selectedPayment === method.id
                        ? 'border-red-500 bg-red-500/10 text-red-400 scale-105'
                        : method.minAmount && order.total_amount < method.minAmount
                          ? 'border-white/10 bg-black/30 opacity-50 cursor-not-allowed'
                          : 'border-white/10 bg-black/30 hover:bg-white/5 hover:border-white/20'
                      }`}
                  >
                    <div className="relative flex items-center w-full">
                      <div className="w-12 h-12 mr-3 bg-white/5 rounded-md flex items-center justify-center">
                        <img src={method.logo} alt={method.name} className="h-8 object-contain"/>
                      </div>
                      <div>
                        <span className="font-semibold text-white text-left">{method.name}</span>
                        <p className="text-xs text-gray-400 text-left">{method.description}</p>
                        {method.minAmount && order.total_amount < method.minAmount && (
                          <p className="text-xs text-red-500 mt-1 text-left">Minimum {formatCurrency(method.minAmount)} FCFA</p>
                        )}
                      </div>
                      {selectedPayment === method.id && <Check className="w-5 h-5 text-red-500 ml-auto" />}
                    </div>
                  </button>
                  
                  {selectedPayment === method.id && (
                    <div className="mt-3 p-4 bg-red-500/5 rounded-b-lg">
                      {selectedPayment === 'mtn' && (
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-1">Numéro de téléphone</label>
                          <Input 
                            id="phone" 
                            type="tel" 
                            value={phoneNumber} 
                            onChange={handlePhoneNumberChange} 
                            placeholder="237xxxxxxxxx" 
                            className="bg-black/80 border-white/20 text-white placeholder-gray-500 focus:ring-red-500"
                          />
                          {phoneError && <p className="text-red-400 text-xs mt-1">{phoneError}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <Button
                type="submit"
                className="w-full text-lg font-bold py-6 bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/30 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!selectedPayment || processing || (selectedPayment === 'mtn' && !phoneNumber)}
              >
                {processing ? (
                  <>
                    <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Traitement...
                  </>
                ) : (
                  `Payer ${['paypal', 'stripe', 'plisio'].includes(selectedPayment || '') 
                    ? `€${convertFCFAToEUR(order.total_amount).toFixed(2)}` 
                    : `${formatCurrency(order.total_amount)} FCFA`}`
                )}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
